import React, { useEffect } from 'react';
import { formValidation } from '~/util/formValidation';
import { FormGroup } from './FormGroup';

export interface FormFieldType {
    valid: boolean;
    touched: boolean;
    rules: string[];
    invalidRules: string[];
    match?: string;
    maxLength?: string | number;
    matchvalue?: string;
}
export type Fields = { [key: string]: FormFieldType };
function getChildInputs(acc: Fields, child: React.ReactNode): Fields {
    if (!React.isValidElement(child)) return acc;
    // Find form elements and make note of their validation requirements
    if (typeof child?.type === 'string' && (child.type === 'input' || child.type === 'textarea')) {
        const id = child.props?.type === 'radio' ? child.props.name : child.props.id;
        acc[id] = {
            valid: true,
            touched: false,
            rules: [],
            invalidRules: [],
        };
        if (child.props.required) {
            acc[id].rules.push('required');
        }
        if (child.props['data-requireddate'] !== undefined) {
            acc[id].rules.push('requireddate');
        }
        if (child.props.type === 'email') {
            acc[id].rules.push('email');
        }
        if (child.props['data-match'] !== undefined) {
            acc[id].rules.push('match');
            acc[id].match = child.props['data-match'];
        }
        if (typeof child.props.maxLength !== 'undefined') {
            acc[id].rules.push('maxLength');
            acc[id].maxLength = child.props.maxLength;
        }
        if (child.props.type === 'password') {
            acc[id].rules.push('password');
        }
    }
    // Make it a recursive search
    if (React.isValidElement(child)) {
        React.Children.toArray(child.props.children).reduce(getChildInputs, acc);
    }
    return acc;
}

interface FormProps
    extends Omit<
        React.DetailedHTMLProps<React.FormHTMLAttributes<HTMLFormElement>, HTMLFormElement>,
        'onSubmit'
    > {
    id?: string;
    className?: string;
    onSubmit: (touchedFields: string[]) => void;
    values: { [key: string]: any };
    children: React.ReactNode;
    disabled?: boolean;
}
export function Form(props: FormProps) {
    const { children, values, onSubmit } = props;
    const [submitted, setSubmitted] = React.useState(false);
    const [valid, setValid] = React.useState(true);
    const [fields, setFields] = React.useState<Fields>(
        React.Children.toArray(children).reduce<Fields>(getChildInputs, {})
    );
    const prevValues = React.useRef(values);
    const mounted = React.useRef(false);

    // updates a single property of a field
    const setFieldState = React.useCallback(
        (prop: string, field: keyof FormFieldType, val: FormFieldType[keyof FormFieldType]) => {
            setFields((prev: Fields) => ({
                ...prev,
                [prop]: {
                    ...prev[prop],
                    [field]: val,
                },
            }));
        },
        []
    );

    // validates all fields
    const validate = React.useCallback(() => {
        let formValid = true;
        Object.keys(fields).forEach((prop) => {
            const { propValid, propValidation } = formValidation(fields[prop], values[prop]);
            setFieldState(prop, 'valid', propValid);
            setFieldState(prop, 'invalidRules', propValidation);
            if (!propValid) formValid = false;
        });
        setValid(formValid);
        return formValid;
    }, [setFieldState, fields, values]);

    // validate fields on mount
    useEffect(() => {
        if (!mounted.current) {
            validate();
            mounted.current = true;
        }
    }, [validate]);

    // update fields when a child is added/removed
    useEffect(() => {
        setFields((prevFields) => {
            const updatedFields = { ...prevFields };
            const childrenInputs = React.Children.toArray(children).reduce<Fields>(
                getChildInputs,
                {}
            );
            Object.keys(childrenInputs).forEach((prop) => {
                if (!updatedFields[prop]) {
                    updatedFields[prop] = childrenInputs[prop];
                }
            });
            Object.keys(updatedFields).forEach((prop) => {
                if (typeof childrenInputs[prop] === 'undefined') {
                    delete updatedFields[prop];
                }
            });
            return updatedFields;
        });
    }, [children]);

    // validate fields on values change
    useEffect(() => {
        Object.keys(prevValues.current).forEach((prop) => {
            if (fields[prop]) {
                if (fields[prop].rules.includes('match') && fields[prop].match) {
                    if (values[fields[prop].match!] !== prevValues.current[fields[prop].match!]) {
                        setFieldState(prop, 'matchvalue', values[fields[prop].match!]);
                    }
                }
                if (values[prop] !== prevValues.current[prop]) {
                    setFieldState(prop, 'touched', true);
                    validate();
                }
            }
        });
        prevValues.current = values;
    }, [values, fields, setFieldState, validate]);

    const getTouchedFields = () => {
        const touchedFields: string[] = [];
        Object.entries(fields).forEach(([key, field]) => {
            if (field.touched) {
                touchedFields.push(key);
            }
        });
        return touchedFields;
    };

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        setSubmitted(true);
        if (validate()) {
            if (typeof onSubmit === 'function') {
                onSubmit(getTouchedFields());
            }
        }
    };

    const validationClasses = React.useMemo(() => {
        const classes = ['', 'zp-form'];
        if (submitted) {
            classes.push('submitted');
        }
        if (valid) {
            classes.push('valid');
        } else {
            classes.push('invalid');
            Object.keys(fields).forEach((prop) => {
                if (!fields[prop].valid) {
                    classes.push(`invalid-${prop}`);
                }
            });
        }
        return classes.join(' ');
    }, [fields, submitted, valid]);

    const displayChildren = (child: React.ReactNode, i: number): React.ReactNode => {
        // On render, pass validation props down to FormGroup so that it can modify display classes
        if (
            React.isValidElement(child) &&
            child.type === FormGroup &&
            child.props.htmlFor &&
            fields[child.props.htmlFor]
        ) {
            // eslint-disable-next-line no-param-reassign
            child = React.cloneElement(child as React.ReactElement, {
                validation: fields[child.props.htmlFor],
                submitted,
                key: child.props.htmlFor,
            });
        }
        if (React.isValidElement(child)) {
            // eslint-disable-next-line no-param-reassign
            child = React.cloneElement(child as React.ReactElement, {
                children: React.Children.map((child as React.ReactElement).props.children, (x, j) =>
                    displayChildren(x, j)
                ),
                key: i,
            });
        }
        return child;
    };

    return (
        <form
            {...props}
            noValidate
            onSubmit={handleSubmit}
            className={(props.className || '') + validationClasses}
        >
            {React.Children.map(children, (x, i) => displayChildren(x, i))}
        </form>
    );
}
